import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { db } from '../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './HelpMe.css';

const HelpMe = () => {
    const { currentUser, userData } = useAuth();
    const { showToast } = useToast();
    const [welfareLeader, setWelfareLeader] = useState({
        name: 'Welfare Leader',
        phone: '+254114963959'
    });
    const [copySuccess, setCopySuccess] = useState(false);
    const [loading, setLoading] = useState(true);

    // Help message template with placeholders
    const helpMessageTemplate = "I am [Your Name], a [Year Name], a member of [Family Name] family within the ministry. I have [Problem Description] and I humbly request for the ministry's intervention. You can contact me via [Your Phone Number]. Thank you.";

    // Pre-filled message with user data if available
    const getPrefilledMessage = () => {
        let message = helpMessageTemplate;
        
        if (currentUser && userData) {
            // Replace [Your Name] with user's name
            if (userData.fullName || userData.displayName) {
                message = message.replace('[Your Name]', userData.fullName || userData.displayName);
            }
            
            // Replace [Your Phone Number] with user's phone
            if (userData.phone) {
                message = message.replace('[Your Phone Number]', userData.phone);
            }
            
            // Replace [Year Name] with user's year group
            if (userData.yearName) {
                message = message.replace('[Year Name]', userData.yearName);
            }
        }
        
        return message;
    };

    const [helpMessage, setHelpMessage] = useState(getPrefilledMessage());

    // Fetch welfare leader information
    useEffect(() => {
        const fetchWelfareLeader = async () => {
            setLoading(true);
            try {
                const adminsRef = collection(db, "admins");

                // Try to find by department field first
                const q = query(
                    adminsRef,
                    where("department", "==", "Welfare")
                );

                const snapshot = await getDocs(q);

                if (!snapshot.empty) {
                    const adminData = snapshot.docs[0].data();
                    setWelfareLeader({
                        name: adminData.fullName || adminData.name || 'Welfare Leader',
                        phone: adminData.phone || '+254114963959'
                    });
                    console.log('Found welfare leader by department:', adminData.fullName);
                    return;
                }

                // Try role field as fallback
                const roleQuery = query(
                    adminsRef,
                    where("role", "==", "Welfare Leader")
                );

                const roleSnapshot = await getDocs(roleQuery);

                if (!roleSnapshot.empty) {
                    const adminData = roleSnapshot.docs[0].data();
                    setWelfareLeader({
                        name: adminData.fullName || adminData.name || 'Welfare Leader',
                        phone: adminData.phone || '+254114963959'
                    });
                    console.log('Found welfare leader by role:', adminData.fullName);
                    return;
                }

                // Try isDepartmentLeader field as last resort
                const leaderQuery = query(
                    adminsRef,
                    where("isDepartmentLeader", "==", "welfare")
                );

                const leaderSnapshot = await getDocs(leaderQuery);

                if (!leaderSnapshot.empty) {
                    const adminData = leaderSnapshot.docs[0].data();
                    setWelfareLeader({
                        name: adminData.fullName || adminData.name || 'Welfare Leader',
                        phone: adminData.phone || '+254114963959'
                    });
                    console.log('Found welfare leader by isDepartmentLeader:', adminData.fullName);
                    return;
                }

                // If no welfare leader found, keep default
                console.log('No welfare leader found, using default');

            } catch (error) {
                console.error("Error fetching welfare leader:", error);
                // Keep default values on error
            } finally {
                setLoading(false);
            }
        };

        fetchWelfareLeader();
    }, []);

    // Update message when user data changes
    useEffect(() => {
        if (currentUser && userData) {
            setHelpMessage(getPrefilledMessage());
        }
    }, [currentUser, userData]);

    const handleCopyMessage = () => {
        navigator.clipboard.writeText(helpMessage).then(() => {
            setCopySuccess(true);
            showToast('Message copied to clipboard!', 'success');
            setTimeout(() => {
                setCopySuccess(false);
            }, 3000);
        }).catch(err => {
            console.error('Could not copy text: ', err);
            showToast('Failed to copy message', 'error');
        });
    };

    const handleWhatsAppClick = () => {
        // Encode the message for WhatsApp
        const encodedMessage = encodeURIComponent(helpMessage);
        const whatsappUrl = `https://wa.me/${welfareLeader.phone.replace(/\D/g, '')}?text=${encodedMessage}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleReplacePlaceholders = () => {
        // Open a simple prompt to replace placeholders
        let newMessage = helpMessage;
        
        // Ask for name if not filled
        if (newMessage.includes('[Your Name]')) {
            const name = prompt('Enter your full name:', userData?.fullName || '');
            if (name) newMessage = newMessage.replace('[Your Name]', name);
        }
        
        // Ask for year name if not filled
        if (newMessage.includes('[Year Name]')) {
            const year = prompt('Enter your year group (e.g., PHILADELPHIAN):', userData?.yearName || '');
            if (year) newMessage = newMessage.replace('[Year Name]', year);
        }
        
        // Ask for family name
        if (newMessage.includes('[Family Name]')) {
            const family = prompt('Enter your family name:');
            if (family) newMessage = newMessage.replace('[Family Name]', family);
        }
        
        // Ask for problem description
        if (newMessage.includes('[Problem Description]')) {
            const problem = prompt('Briefly describe your need:');
            if (problem) newMessage = newMessage.replace('[Problem Description]', problem);
        }
        
        // Ask for phone if not filled
        if (newMessage.includes('[Your Phone Number]')) {
            const phone = prompt('Enter your phone number:', userData?.phone || '254');
            if (phone) newMessage = newMessage.replace('[Your Phone Number]', phone);
        }
        
        setHelpMessage(newMessage);
        showToast('Placeholders updated!', 'success');
    };

    return (
        <div className="help-me-page">
            <div className="help-me-main-content">
                <div className="help-me-section">
                    <h2 className="help-me-section-title">Request for Help</h2>
                    <p className="help-me-intro">
                        We at White Doves Ministry are here to support you in your time of need. 
                        Please follow the steps below to request assistance:
                    </p>

                    <div className="help-me-message-container">
                        <div className="help-me-message-box">
                            <p className="help-me-message">{helpMessage}</p>
                        </div>

                        <div className="help-me-button-group">
                            <button 
                                className="help-me-btn help-me-btn-primary"
                                onClick={handleCopyMessage}
                            >
                                <i className="fas fa-copy"></i> Copy Message
                            </button>
                            <button 
                                className="help-me-btn help-me-btn-secondary"
                                onClick={handleReplacePlaceholders}
                            >
                                <i className="fas fa-edit"></i> Edit Placeholders
                            </button>
                        </div>

                        {copySuccess && (
                            <div className="help-me-copy-success">
                                <i className="fas fa-check-circle"></i> Message copied to clipboard!
                            </div>
                        )}
                    </div>

                    <div className="help-me-instructions">
                        <h3>Instructions:</h3>
                        <ol className="help-me-steps">
                            <li>
                                <span className="step-number">1</span>
                                <span>Click the "Copy Message" button above</span>
                            </li>
                            <li>
                                <span className="step-number">2</span>
                                <span>Click the WhatsApp button below</span>
                            </li>
                            <li>
                                <span className="step-number">3</span>
                                <span>Paste the message in WhatsApp</span>
                            </li>
                            <li>
                                <span className="step-number">4</span>
                                <span>Replace any remaining placeholders with your information</span>
                            </li>
                            <li>
                                <span className="step-number">5</span>
                                <span>Send the message to the ministry's Welfare Leader.</span>
                            </li>
                        </ol>
                    </div>
                </div>

                <div className="help-me-bible-verse">
                    <p>"Come to me, all you who are weary and burdened, and I will give you rest."</p>
                    <p className="verse-reference">- Matthew 11:28</p>
                </div>

                <div className="help-me-emergency">
                    <h3 className="emergency-title">
                        <i className="fas fa-exclamation-triangle"></i> For Emergency Support
                    </h3>
                    <p>Contact our Welfare Leader:</p>
                    {loading ? (
                        <div className="emergency-loading">
                            <i className="fas fa-spinner fa-spin"></i> Loading...
                        </div>
                    ) : (
                        <>
                            <p className="emergency-name">
                                <i className="fas fa-user"></i> {welfareLeader.name}
                            </p>
                            <p className="emergency-phone">
                                <i className="fas fa-phone"></i>
                                <a href={`tel:${welfareLeader.phone.replace(/\D/g, '')}`}>
                                    {welfareLeader.phone}
                                </a>
                            </p>
                        </>
                    )}
                </div>

                {/* WhatsApp Action Button */}
                <div className="help-me-whatsapp-action">
                    <button 
                        className="help-me-whatsapp-button"
                        onClick={handleWhatsAppClick}
                        disabled={loading}
                    >
                        <i className="fab fa-whatsapp"></i>
                        {loading ? 'Loading...' : `Message ${welfareLeader.name} on WhatsApp`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HelpMe;
